import { useEffect, useId, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { shallowEqual } from 'react-redux';
import { applyPromoCode } from '../sagas/sagaActions';
import {
  selectCurrencySymbol,
  selectPayPalHidden,
  selectReadOnly,
} from '../store/appSelectors';
import {
  selectAppliedPromoCode,
  selectCartProducts,
  selectPreviewTotals,
  selectPromoApplied,
  selectPromoCode,
  selectPromoDiscount,
  selectPromoInvalid,
} from '../store/cartSelectors';
import { setPromoCode } from '../store/cartSlice';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { selectActiveNotices } from '../store/noticeSelectors';
import { billingCadence, licenseTypeLabel } from './catalogLabels';
import { currency } from './data';
import {
  AmericanExpress,
  AttentionIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CloseIcon,
  Discover,
  LockIcon,
  Mastercard,
  PayPal,
  StarIcon,
  Visa,
  WeChat,
} from './icons';
import type { NoticeRule } from './notices';
import styles from './primitives.module.scss';

export function BenefitItem({
  label,
  variant = 'default',
}: {
  label: string;
  variant?: 'default' | 'compact';
}) {
  return (
    <li
      className={`${styles.benefit} ${variant === 'compact' ? styles.compact : ''}`}
    >
      <CheckIcon className={styles.benefitCheck} />
      <span className={styles.benefitLabel}>{label}</span>
    </li>
  );
}

export function QuantityField({
  value,
  minAmt = 1,
  maxAmt,
  onChange,
}: {
  value: number;
  minAmt?: number;
  maxAmt?: number;
  onChange: (next: number) => void;
}) {
  const id = useId();
  const { t } = useTranslation();
  const [draft, setDraft] = useState(String(value));
  const valueRef = useRef(value);
  const readOnly = useAppSelector(selectReadOnly);
  const locked = readOnly || (maxAmt !== undefined && maxAmt <= minAmt);

  useEffect(() => {
    valueRef.current = value;
    setDraft(String(value));
  }, [value]);

  const withinLimits = (next: number) =>
    maxAmt === undefined
      ? Math.max(minAmt, next)
      : Math.min(Math.max(minAmt, next), maxAmt);

  const commit = (next: number) => {
    valueRef.current = next;
    setDraft(String(next));
    onChange(next);
  };

  const handleChange = (raw: string) => {
    const digits = raw.replace(/[^0-9]/g, '');
    setDraft(digits);
    if (digits !== '') {
      const next = withinLimits(Number.parseInt(digits, 10));
      valueRef.current = next;
      onChange(next);
    }
  };

  const handleBlur = () => {
    const parsed = Number.parseInt(draft, 10);
    if (draft === '' || parsed !== withinLimits(parsed)) {
      commit(withinLimits(Number.isNaN(parsed) ? minAmt : parsed));
    }
  };

  const step = (delta: number) => {
    commit(withinLimits(valueRef.current + delta));
  };

  return (
    <div className={styles.qtyField}>
      <label className={styles.qtyLabel} htmlFor={id}>
        {t('Quantity')}
      </label>
      <div className={`${styles.qtyBox} ${locked ? styles.qtyBoxLocked : ''}`}>
        <input
          id={id}
          type="text"
          inputMode="numeric"
          className={styles.qtyInput}
          value={draft}
          disabled={locked}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
          }}
        />
        {locked ? (
          <LockIcon className={styles.qtyLock} />
        ) : (
          <div className={styles.qtyArrows}>
            <button
              type="button"
              aria-label={t('Increase quantity')}
              onClick={() => step(1)}
              className={styles.qtyArrow}
            >
              <ChevronUpIcon />
            </button>
            <button
              type="button"
              aria-label={t('Decrease quantity')}
              onClick={() => step(-1)}
              className={styles.qtyArrow}
            >
              <ChevronDownIcon />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function RemoveButton({ onClick }: { onClick?: () => void }) {
  const { t } = useTranslation();
  return (
    <button type="button" onClick={onClick} className={styles.remove}>
      {t('Remove')}
    </button>
  );
}

export function PriceTag({ price, note }: { price: number; note?: string }) {
  const currencySymbol = useAppSelector(selectCurrencySymbol);

  return (
    <div>
      <div className={styles.price}>
        <span className={styles.priceAmount}>
          {currencySymbol} {Math.floor(price)}
        </span>
        <sup className={styles.priceSup}>{currency(price).split('.')[1]}</sup>
      </div>
      {note && <div className={styles.priceNote}>{note}</div>}
    </div>
  );
}

export function PromoCodeField() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const promoCode = useAppSelector(selectPromoCode);
  const appliedPromoCode = useAppSelector(selectAppliedPromoCode);
  const promoApplied = useAppSelector(selectPromoApplied);
  const promoInvalid = useAppSelector(selectPromoInvalid);
  const readOnly = useAppSelector(selectReadOnly);
  const [open, setOpen] = useState(Boolean(appliedPromoCode));

  if (readOnly) {
    return null;
  }

  const labelClass = promoApplied
    ? `${styles.promoLabel} ${styles.promoLabelOk}`
    : promoInvalid
      ? `${styles.promoLabel} ${styles.promoLabelError}`
      : styles.promoLabel;
  const inputClass = promoApplied
    ? `${styles.promoInput} ${styles.promoInputOk}`
    : promoInvalid
      ? `${styles.promoInput} ${styles.promoInputError}`
      : styles.promoInput;

  return (
    <div className={styles.promo}>
      <button
        type="button"
        className={styles.promoToggle}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className={labelClass}>{t('Promo code')}</span>
        {open ? (
          <ChevronUpIcon className={styles.promoChevron} />
        ) : (
          <ChevronDownIcon className={styles.promoChevron} />
        )}
      </button>
      <div
        className={
          open
            ? `${styles.promoBody} ${styles.promoBodyOpen}`
            : styles.promoBody
        }
      >
        <form
          className={styles.promoRow}
          onSubmit={(e) => {
            e.preventDefault();
            const trimmed = promoCode.trim();
            if ((!trimmed && !appliedPromoCode) || trimmed === appliedPromoCode)
              return;
            dispatch(setPromoCode(trimmed));
            dispatch(applyPromoCode(trimmed));
          }}
        >
          <div className={styles.promoInputWrap}>
            <input
              type="text"
              value={promoCode}
              onChange={(e) => dispatch(setPromoCode(e.target.value))}
              className={inputClass}
            />
            {appliedPromoCode && (
              <button
                type="button"
                aria-label={t('Remove promo code')}
                className={styles.promoClear}
                onClick={() => {
                  dispatch(setPromoCode(''));
                  dispatch(applyPromoCode(''));
                }}
              >
                <CloseIcon className={styles.promoClearIcon} />
              </button>
            )}
          </div>
          <button type="submit" className={styles.promoApply}>
            {t('Apply')}
          </button>
        </form>
        {promoApplied && (
          <div className={`${styles.promoMsg} ${styles.promoMsgOk}`}>
            {t('Promo code applied.')}
          </div>
        )}
        {promoInvalid && (
          <div className={`${styles.promoMsg} ${styles.promoMsgError}`}>
            {t('Please enter a valid promo code.')}
          </div>
        )}
      </div>
    </div>
  );
}

function Notice({ notice }: { notice: NoticeRule }) {
  const { t } = useTranslation();
  const isSuccess = notice.variant === 'success';
  const href = notice.bodyLink?.();
  return (
    <div
      className={`${styles.notice} ${isSuccess ? styles.noticeSuccess : ''}`}
    >
      <div className={styles.noticeTitle}>
        {!isSuccess && <AttentionIcon className={styles.noticeIcon} />}
        {t(notice.title)}
      </div>
      {notice.body && (
        <div className={styles.noticeBody}>
          {href ? (
            <Trans
              i18nKey={notice.body}
              components={{
                // biome-ignore lint/a11y/useAnchorContent: Trans fills the anchor at render time
                link: <a href={href} target="_blank" rel="noreferrer" />,
              }}
            />
          ) : (
            t(notice.body)
          )}
        </div>
      )}
    </div>
  );
}

export function NoticeList() {
  const notices = useAppSelector(selectActiveNotices, shallowEqual);
  return (
    <>
      {notices.map((notice) => (
        <Notice key={notice.id} notice={notice} />
      ))}
    </>
  );
}

export function OrderLineItems() {
  const { t } = useTranslation();
  const products = useAppSelector(selectCartProducts);
  const currencySymbol = useAppSelector(selectCurrencySymbol);

  return (
    <ul className={styles.lines}>
      {products.map((product) => (
        <li key={product.ratePlan} className={styles.line}>
          <div>
            <div className={styles.lineName}>{product.name}</div>
            <div className={styles.lineMeta}>
              {t(
                'Quantity: {{quantity}} | {{licenseType}}, billed {{cadence}}',
                {
                  quantity: product.quantity,
                  licenseType: t(licenseTypeLabel(product.licenseType)),
                  cadence: t(billingCadence(product.billingPeriod)),
                },
              )}
            </div>
          </div>
          <div className={styles.linePrice}>
            {currencySymbol} {currency(product.price)}
          </div>
        </li>
      ))}
    </ul>
  );
}

export function OrderTotals() {
  const { t } = useTranslation();
  const { subtotal, taxes, total } = useAppSelector(selectPreviewTotals);
  const promoDiscount = useAppSelector(selectPromoDiscount);
  const currencySymbol = useAppSelector(selectCurrencySymbol);

  return (
    <div className={styles.totals}>
      <dl className={styles.totalsSub}>
        <div className={`${styles.totalsRow} ${styles.totalsRowSub}`}>
          <dt>{t('Subtotal')}</dt>
          <dd className={styles.totalsValueSemi}>
            {currencySymbol} {currency(subtotal)}
          </dd>
        </div>
        {promoDiscount && (
          <div className={`${styles.totalsRow} ${styles.totalsRowDiscount}`}>
            <dt>{promoDiscount.name}</dt>
            <dd>
              -{currencySymbol} {currency(Math.abs(promoDiscount.amount))}
            </dd>
          </div>
        )}
        <div className={`${styles.totalsRow} ${styles.totalsRowSmall}`}>
          <dt>{t('Taxes')}</dt>
          <dd>
            {currencySymbol} {currency(taxes)}
          </dd>
        </div>
      </dl>
      <div className={styles.totalsGrand}>
        <span className={styles.totalsGrandLabel}>{t('Total')}</span>
        <span className={styles.totalsGrandValue}>
          {currencySymbol} {currency(total)}
        </span>
      </div>
    </div>
  );
}

export function Stars({ rating }: { rating: number }) {
  return (
    <div className={styles.stars}>
      {[0, 1, 2, 3, 4].map((i) => {
        const fill = Math.max(0, Math.min(1, rating - i));
        return (
          <span key={i} className={styles.star}>
            <StarIcon className={styles.starBase} />
            <span
              className={styles.starFill}
              style={{ width: `${fill * 100}%` }}
            >
              <StarIcon />
            </span>
          </span>
        );
      })}
    </div>
  );
}

export function PaymentMethods() {
  const { t } = useTranslation();
  const payPalHidden = useAppSelector(selectPayPalHidden);

  return (
    <div className={styles.payments}>
      <div className={styles.payLabel}>{t('We accept')}</div>
      <div className={styles.payList}>
        <Mastercard />
        <Visa />
        <AmericanExpress />
        <Discover />
        {!payPalHidden && <PayPal />}
        <WeChat />
      </div>
    </div>
  );
}
